import React, { useEffect, useState } from "react";
import { UserButton } from "@clerk/clerk-react";
import { ChevronsLeft, ChevronsRight, MessageSquare } from "lucide-react";
import { Button } from "components/shadcn/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "components/shadcn/ui/tooltip";
import { useSidebar } from "./useSidebar";
import { useSelector } from "react-redux";
import { selectUnreadMessageCount } from "components/features/messaging-thread/messagingThreadSlice";

const Sidebar: React.FC = () => {
  const unreadMessageCount = useSelector(selectUnreadMessageCount);
  const { collapsed, setCollapsed, navItems, handleNavigation } = useSidebar();

  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const sidebar = document.getElementById("sidebar");
      if (!sidebar) return;

      const rect = sidebar.getBoundingClientRect();
      const overSidebar =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      setIsHovered(overSidebar);
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    setCollapsed(!isHovered);
  }, [isHovered, setCollapsed]);

  return (
    <TooltipProvider>
      <div
        id="sidebar"
        className={`h-screen flex flex-col bg-white border-r transition-all duration-300 ${
          collapsed ? "w-[70px]" : "w-60"
        }`}
      >
        <div className="relative flex items-center justify-center px-3 py-4 border-b bg-white shrink-0">
          <img
            src={collapsed ? "/assets/bcLetterLogo.png" : "/assets/bcLogo.png"}
            alt="Logo"
            className="h-8 object-contain transition-all duration-300"
          />

          <div
            className="absolute top-1/2 -right-5 transform -translate-y-1/2 z-50 transition-all duration-300 hover:-right-6"
            style={{ pointerEvents: "auto" }}
          >
            <Button
              variant="secondary"
              size="icon"
              className="rounded-full bg-white border shadow-md hover:bg-gray-50 w-8 h-8 flex items-center justify-center"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? (
                <ChevronsRight className="w-4 h-4 text-gray-700" />
              ) : (
                <ChevronsLeft className="w-4 h-4 text-gray-700" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3 p-3 w-full">
            {navItems
              .filter((item: any) => item.visible)
              .map((item: any) => {
                const button = (
                  <Button
                    key={item.key}
                    variant="ghost"
                    className={`w-full flex items-center transition-all duration-300 bg-gray-100 ${
                      collapsed ? "justify-center" : "!justify-start"
                    }`}
                    onClick={() =>
                      handleNavigation(item.path ?? "", item.label ?? "")
                    }
                  >
                    {item.icon}
                    {!collapsed && <span className="ml-3">{item.label}</span>}
                  </Button>
                );

                return collapsed ? (
                  <Tooltip key={item.key}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                ) : (
                  button
                );
              })}
          </div>
        </div>

        <div className="border-t p-4 bg-white shrink-0">
          <div className="flex flex-col items-center gap-3">
            <Button
              variant="outline"
              size={collapsed ? "icon" : "default"}
              className={`relative flex items-center justify-center w-[30%] ${
                !collapsed ? "!bg-gray-100" : ""
              }`}
              onClick={() => handleNavigation("threads", "/threads", true)}
            >
              <MessageSquare className="w-5 h-5" />

              {unreadMessageCount > 0 && (
                <span className="absolute -top-2 -right-3 h-6 w-6 flex items-center justify-center bg-red-500 text-white rounded-full text-[10px] font-extrabold leading-none overflow-hidden">
                  {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                </span>
              )}
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: "w-8 h-8",
                      userButtonPopoverCustomItemButton:
                        "font-normal text-gray-800 font-sans",
                    },
                  }}
                  showName={!collapsed}
                />
              </TooltipTrigger>
              <TooltipContent side="right">Manage Profile</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Sidebar;
